import { useState, useRef, useMemo, useEffect } from "react";
import { useDataCleaningContext } from "../context/data-cleaning-context";
import useCleaningData from "./use-cleaning-data";
import { Field } from "../types/field";
import { DEFAULT_DISPLAY_LIMIT } from "../constants";

interface UseFieldSelectionReturn {
  fields: Field[];
  displayedFields: Field[];
  showAll: boolean;
  isLoading: boolean;
  error: Error | undefined;
  hasSelectedEntity: boolean;
  handleFieldToggle: (id: string) => void;
  handleClearSelection: () => void;
  handleShowAllToggle: () => void;
  refetch: () => void;
}

const useFieldSelection = (
  hasSelectedEntity: boolean,
): UseFieldSelectionReturn => {
  const { selection, setSelectedField, setSelectedEntity } =
    useDataCleaningContext();
  const { getFilteredFields, isLoading, error, refetch } = useCleaningData();

  const [showAll, setShowAll] = useState(false);
  const hasAdvanced = useRef(false);

  // Get filtered fields based on selected clients and filter by selected entity
  const apiFields = useMemo(() => {
    if (selection.selectedClientIds.length === 0 || !selection.selectedEntity)
      return [];

    const allFields = getFilteredFields(selection.selectedClientIds);
    // Filter to only show fields for the selected entity
    return allFields.filter(
      (field) => field.entityName === selection.selectedEntity,
    );
  }, [
    selection.selectedClientIds,
    selection.selectedEntity,
    getFilteredFields,
  ]);

  // Transform API fields to local format
  const initialFields = useMemo(
    () =>
      apiFields.map((field) => ({
        id: `${field.id}`,
        name: field.name,
        count: field.count,
        selected: false,
      })),
    [apiFields],
  );

  const [fields, setFields] = useState<Field[]>(initialFields);

  // Update fields state when API data changes, but preserve selection from context
  useEffect(() => {
    const updatedFields = initialFields.map((field) => {
      // Check if this field matches the one selected in context
      const isSelected =
        selection.selectedFieldId !== undefined &&
        field.id === `${selection.selectedFieldId}`;

      return {
        ...field,
        selected: isSelected,
      };
    });
    setFields(updatedFields);
  }, [initialFields, selection.selectedFieldId]);

  const handleFieldToggle = (id: string) => {
    const clickedField = fields.find((field) => field.id === id);
    if (clickedField?.selected) {
      return; // Don't allow unselecting
    }

    const updatedFields = fields.map((field) => ({
      ...field,
      selected: field.id === id,
    }));
    setFields(updatedFields);

    // Save selected field to context
    const selectedField = apiFields.find((f) => `${f.id}` === id);
    if (selectedField) {
      setSelectedField(selectedField.id);
    }
  };

  const handleClearSelection = () => {
    // Reset to initial state
    setFields(initialFields);
    hasAdvanced.current = false;
    // Trigger context reset for field only (cascades from setSelectedEntity)
    if (selection.selectedEntity && selection.selectedEntityId) {
      setSelectedEntity(selection.selectedEntityId, selection.selectedEntity);
    }
    // Refetch to ensure fresh data
    refetch();
  };

  const handleShowAllToggle = () => {
    setShowAll(!showAll);
  };

  const displayedFields = showAll
    ? fields
    : fields.slice(0, DEFAULT_DISPLAY_LIMIT);

  return {
    fields,
    displayedFields,
    showAll,
    isLoading,
    error,
    hasSelectedEntity,
    handleFieldToggle,
    handleClearSelection,
    handleShowAllToggle,
    refetch,
  };
};

export default useFieldSelection;
